import { Controller } from "@hotwired/stimulus"
import { syncGateReason } from "../lib/gate_reason"

// W-9 form (w9_submissions/new): conditional tax-classification fields, SSN
// masking, client-side validation on submit, and the certification gate —
// the Submit is disabled until the "I certify" box is checked. The server
// validates the record again on POST (W9SubmissionsController#create).
//
// A11Y-1 / BUTTON-4 (#13355): every disabled frame of the Submit says why —
// as the button's own gl-tooltip plus a "<label> — <reason>" accessible name
// (lib/gate_reason) — and both are withdrawn the moment the box enables it.
// Field validation is NOT the gate: an invalid submit is refused inline and
// the certified button stays enabled. A valid submission that is leaving the
// page holds the button with the in-flight reason so it cannot post twice.
export default class extends Controller {
  static targets = [
    "llcFields", "otherFields", "certCheckbox", "submitBtn",
    "streetAddress", "city", "state", "zipCode",
    "taxClassificationGroup", "ssnInput"
  ]
  // The two reasons are authored ONCE, in w9_submissions/new.html.haml, which
  // paints the certification reason server-side on the disabled button and
  // stamps both here as values — so the first paint and every later gate
  // change say the same thing. The defaults only serve a DOM that stamps
  // nothing (the Jest fixtures).
  static values = {
    profileStreet: String,
    profileCity: String,
    profileState: String,
    profileZip: String,
    certifyReason: { type: String, default: "Check the certification box first" },
    submittingReason: { type: String, default: "Submitting your W-9…" }
  }

  connect() {
    this.inFlight = false
    // A back/forward-cache restore brings the page back with the in-flight
    // frame still painted and no controller reconnect; settle to the real gate.
    this._onPageShow = (event) => {
      if (event.persisted) this.settle()
    }
    window.addEventListener("pageshow", this._onPageShow)
    this.syncSubmit()
  }

  disconnect() {
    if (this._onPageShow) window.removeEventListener("pageshow", this._onPageShow)
  }

  copyAddress() {
    if (this.hasStreetAddressTarget) this.streetAddressTarget.value = this.profileStreetValue
    if (this.hasCityTarget) this.cityTarget.value = this.profileCityValue
    if (this.hasStateTarget) this.stateTarget.value = this.profileStateValue
    if (this.hasZipCodeTarget) this.zipCodeTarget.value = this.profileZipValue
  }

  formatSsn() {
    if (!this.hasSsnInputTarget) return
    const input = this.ssnInputTarget
    const digits = input.value.replace(/\D/g, "").slice(0, 9)
    let formatted = digits
    if (digits.length > 5) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
    } else if (digits.length > 3) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`
    }
    input.value = formatted
  }

  changeTaxClassification(event) {
    const value = event.target.value

    if (this.hasLlcFieldsTarget) {
      this.llcFieldsTarget.classList.toggle("d-none", value !== "llc")
    }
    if (this.hasOtherFieldsTarget) {
      this.otherFieldsTarget.classList.toggle("d-none", value !== "other")
    }
    if (this.hasTaxClassificationGroupTarget) {
      this.taxClassificationGroupTarget.classList.remove("is-invalid")
    }
  }

  toggleSubmit() {
    if (!this.hasCertCheckboxTarget) return

    this.syncSubmit()

    if (this.certCheckboxTarget.checked) {
      this.certCheckboxTarget.classList.remove("is-invalid")
    }
  }

  // The Submit's state and reason, from one place: in flight while a valid
  // submission is leaving, the certification reason while the box is
  // unchecked, enabled otherwise — applied as the button's gl-tooltip plus its
  // "<label> — <reason>" name, both withdrawn when enabled (lib/gate_reason).
  syncSubmit() {
    if (!this.hasSubmitBtnTarget) return
    syncGateReason(this.submitBtnTarget, { ready: this.ready(), reason: this.disabledReason() })
  }

  certified() {
    return this.hasCertCheckboxTarget && this.certCheckboxTarget.checked
  }

  ready() {
    return !this.inFlight && this.certified()
  }

  disabledReason() {
    return this.inFlight ? this.submittingReasonValue : this.certifyReasonValue
  }

  // The request is over and the page is still here (bfcache restore): the
  // button's state is whatever the gate says NOW, never a blind re-enable.
  settle() {
    this.inFlight = false
    this.syncSubmit()
  }

  validateForm(event) {
    let valid = true

    // Validate required text/select fields
    this.element.querySelectorAll("[required]").forEach((field) => {
      if (!field.value || !field.value.trim()) {
        field.classList.add("is-invalid")
        valid = false
      } else {
        field.classList.remove("is-invalid")
      }
    })

    // Validate tax classification radio group
    const taxRadios = this.element.querySelectorAll(
      'input[name="w9_submission[tax_classification]"]'
    )
    const taxSelected = Array.from(taxRadios).some((r) => r.checked)
    if (!taxSelected && this.hasTaxClassificationGroupTarget) {
      this.taxClassificationGroupTarget.classList.add("is-invalid")
      valid = false
    }

    // Validate conditional required fields
    if (this._isLlcSelected() && this.hasLlcFieldsTarget) {
      const llcSelect = this.llcFieldsTarget.querySelector("select")
      if (llcSelect && !llcSelect.value) {
        llcSelect.classList.add("is-invalid")
        valid = false
      }
    }

    if (this._isOtherSelected() && this.hasOtherFieldsTarget) {
      const otherInput = this.otherFieldsTarget.querySelector("input")
      if (otherInput && !otherInput.value.trim()) {
        otherInput.classList.add("is-invalid")
        valid = false
      }
    }

    // Validate SSN (must be 9 digits)
    if (this.hasSsnInputTarget) {
      const ssnDigits = this.ssnInputTarget.value.replace(/\D/g, "")
      if (ssnDigits.length !== 9) {
        this.ssnInputTarget.classList.add("is-invalid")
        valid = false
      }
    }

    // Validate certification checkbox
    if (this.hasCertCheckboxTarget && !this.certCheckboxTarget.checked) {
      this.certCheckboxTarget.classList.add("is-invalid")
      valid = false
    }

    if (!valid) {
      event.preventDefault()
      const firstInvalid = this.element.querySelector(".is-invalid")
      if (firstInvalid && firstInvalid.scrollIntoView) {
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" })
      }
      return
    }

    // The submission is leaving: hold the button with the in-flight reason so
    // a second click (or Enter) cannot record the W-9 twice. Disabling the
    // submitter inside its own submit event never cancels the submission.
    this.inFlight = true
    this.syncSubmit()
  }

  clearValidation(event) {
    const field = event.target
    if (field.value && field.value.trim()) {
      field.classList.remove("is-invalid")
    }
  }

  // private

  _isLlcSelected() {
    const checked = this.element.querySelector(
      'input[name="w9_submission[tax_classification]"]:checked'
    )
    return checked && checked.value === "llc"
  }

  _isOtherSelected() {
    const checked = this.element.querySelector(
      'input[name="w9_submission[tax_classification]"]:checked'
    )
    return checked && checked.value === "other"
  }
}
